import type { AIMetadata } from '@/types'
import { supabase } from '@/lib/supabase'

interface RouteParams {
  cloudinary_url: string
  media_type:     string
  file_name:      string
  event_name?:    string
  participant_role?: string
}

export async function extractMediaMetadata(params: RouteParams): Promise<AIMetadata> {
  try {
    const url  = import.meta.env.VITE_SUPABASE_URL as string
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string

    const res = await fetch(`${url}/functions/v1/claude-router`, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'apikey':        anon,
        'Authorization': `Bearer ${anon}`,
      },
      body: JSON.stringify(params),
    })

    if (!res.ok) throw new Error(`Edge function returned ${res.status}`)
    const data = await res.json()
    if (!data || typeof data.route_score === 'undefined') throw new Error('Invalid AI response shape')
    return data as AIMetadata
  } catch (err) {
    console.warn('[Claude] Edge function unavailable, using fallback:', err)
    return buildFallback(params)
  }
}

function buildFallback(p: RouteParams): AIMetadata {
  const score = p.media_type === 'video' ? 0.88 : 0.74
  return {
    tags:               ['the-room', p.event_name ?? 'live-event', p.media_type, 'ibiza-2026'],
    description:        `${p.media_type} uploaded to The Room — full AI analysis pending.`,
    mood:               'vibrant',
    route_score:        score + Math.random() * 0.08,
    routing_reason:     'Auto-tagged. Claude managed agent will process when available.',
    suggested_audiences: ['creators', 'press'],
  }
}

export async function generatePressPack(input: {
  event_name:          string
  event_date:          string
  event_venue:         string
  organiser_name:      string
  tone:                string
  include_highlights:  boolean
}): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('press-pack', { body: input })
    if (error) throw error
    return data?.press_pack ?? buildFallbackPack(input)
  } catch {
    return buildFallbackPack(input)
  }
}

function buildFallbackPack(i: {
  event_name:         string
  event_date:         string
  event_venue:        string
  organiser_name:     string
  tone:               string
  include_highlights: boolean
}): string {
  const media = i.include_highlights
    ? '\nMEDIA\nThe Room will provide a curated photo and video pack to accredited press within 24 hours of the event closing.\n'
    : ''

  if (i.tone === 'hype') {
    return `🔥 ${i.event_name.toUpperCase()} 🔥\n${i.event_venue} · ${i.event_date}\n\nThis is the one. ${i.organiser_name} bring you an experience engineered to leave a mark.\n${media}\nPress: press@theroom.io`
  }
  if (i.tone === 'formal') {
    return `PRESS RELEASE — FOR IMMEDIATE RELEASE\n\n${i.organiser_name} Announces ${i.event_name}\n${i.event_venue}, ${i.event_date}\n${media}\nEnquiries: press@theroom.io\n\n— ENDS —`
  }
  return `${i.event_name}\n${i.event_venue} · ${i.event_date}\n\nSome events leave a mark. ${i.event_name} is one of them. ${i.organiser_name} have built something worth showing up for.\n${media}\npress@theroom.io`
}
